/* ---------------------------------------------------------------------------
   /platform/showcase — media showcase, ported from the Next.js 3D site
   (app/platform/showcase/{page,showcase-client,media}.ts) into this repo's
   own stack: Create React App + react-router, plain JS, no CSS custom
   properties to lean on. Every design-token value below (spacing, duration,
   easing, the pill radius, the primary button colours) is the LITERAL value
   copied from the source site's globals.css, not a guess — see the comment
   on each block. Fonts are swapped to what this repo actually loads:
   Space Grotesk for display/headings (.font-display, already used by
   VisoPlatformPage), JetBrains Mono for the data voice (rail ticks, file
   paths, the slide counter), Inter inherited for body copy.

   UNLINKED and UNFINISHED, same as the source: not in Header's nav, and
   flagged noindex via a local <meta name="robots"> — see the effect below,
   since this repo's SeoTags/seoForPath has no noindex mechanism to hook into
   yet and this page should not need to add one for everyone.

   Zero assets exist yet. Every empty slot names the exact file it is
   waiting for (see ../data/showcaseMedia.js), and both download buttons
   render disabled until a real path is supplied.
--------------------------------------------------------------------------- */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  SHOWCASE_TABS,
  PREVIOUS_PRODUCTS,
  SLIDER_FRAMES,
  VIDEO_TILES,
  expectedImagePath,
  expectedVideoPath,
  posterFor,
  youTubeId,
} from "@/data/showcaseMedia";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import client from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

/* ---- tokens — literal values copied from the source site's globals.css --- */
const CANVAS_DARK = "#0A0B0E";
const SURFACE_DARK = "#101216";
const TXT_D1 = "#F4F5F7";
const TXT_D2 = "#A6ADB8";
const TXT_D3 = "#6B7078";
const BORDER_D = "rgba(244,245,247,0.10)";
const BORDER_D_STRONG = "rgba(244,245,247,0.18)";
const DATUM = "rgba(244,245,247,0.18)";
const MARK = "rgba(244,245,247,0.32)";
const SIGNAL = "#ED510C";
const PRIMARY_BG = "#F4F5F7"; // --interactive-dark-primary-bg
const PRIMARY_FG = "#0A0B0E"; // --interactive-dark-primary-fg
const PILL = "999px"; // --radius-r-pill

// this repo's actual fonts (index.css), not the source site's Archivo/Plex Mono
const mono = "'JetBrains Mono', Menlo, monospace";
const sans = "'Space Grotesk', 'Inter', sans-serif"; // headings only — body inherits Inter

const DUR_1 = "120ms"; // --duration-dur-1: hover/focus/pressed
const DUR_2 = "240ms"; // --duration-dur-2: component transitions
const EASE = "cubic-bezier(0.32, 0, 0.16, 1)"; // --ease-standard

const TABBAR_H = 56;
const NAV_D = 72;
const NAV_M = 64;
const RULER_H = 44;

const RAIL_TOP = NAV_D + TABBAR_H; // 128
const RULER_TOP = NAV_M + TABBAR_H; // 120
const SCROLL_MT_D = RAIL_TOP; // 128
const SCROLL_MT_M = RULER_TOP + RULER_H; // 164

const RAIL_GUTTER = 25;
const RAIL_W = 140;

const SLIDE_MS = 6000;

// --spacing-s{n}, literal px (source: 8px base scale)
const S = {
  s3: "12px",
  s4: "16px",
  s6: "24px",
  s8: "32px",
  s12: "48px",
  s16: "64px",
};

const eyebrow = (color) => ({
  fontFamily: mono,
  fontSize: 13,
  fontWeight: 500,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color,
});

function reduced() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

/* Reduced-motion as an external store rather than useState-in-an-effect, so
   the first client render already has the right answer (no motion flash)
   and an OS setting changed mid-visit is picked up for free. */
const RM_QUERY = "(prefers-reduced-motion: reduce)";
function subscribeRM(cb) {
  const m = window.matchMedia(RM_QUERY);
  m.addEventListener("change", cb);
  return () => m.removeEventListener("change", cb);
}
const getRM = () => window.matchMedia(RM_QUERY).matches;
const getRMServer = () => false;

/** true when motion is ALLOWED. */
function useMotionOk() {
  return !useSyncExternalStore(subscribeRM, getRM, getRMServer);
}

/** DOM id for a product section. Slugs repeat across tabs (Cargo Vision is in
    both Yard and Warehouse) so the tab is part of the id; the media folder
    still keys off the bare slug — one folder per product, shared by every
    environment that ships it. */
function sectionId(tabId, slug) {
  return `${tabId}--${slug}`;
}

function jumpTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: reduced() ? "auto" : "smooth", block: "start" });
}

/* =========================================================================
   SCROLL-SPY
   ========================================================================= */
function useActiveSection(ids) {
  const [active, setActive] = useState(null);
  const activeRef = useRef(null);
  const key = ids.join("|");

  useEffect(() => {
    const list = key ? key.split("|") : [];
    const compute = () => {
      const line = window.innerHeight * 0.4;
      let next = list[0] ?? null;
      for (const id of list) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) next = id;
      }
      if (next !== activeRef.current) {
        activeRef.current = next;
        setActive(next);
      }
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [key]);

  return active;
}

/* =========================================================================
   THE RAIL
   ========================================================================= */
function ticksFor(tab) {
  return tab.products.map((prod, i) => ({
    n: String(i + 1).padStart(2, "0"),
    label: prod.name.replace(/\s+Vision$/i, "").toUpperCase(),
    id: sectionId(tab.id, prod.slug),
  }));
}

function ShowcaseRailDesktop({ ticks }) {
  const active = useActiveSection(ticks.map((t) => t.id));
  const containerRef = useRef(null);
  const itemRefs = useRef({});
  const [dotTop, setDotTop] = useState(null);

  useEffect(() => {
    if (!active) {
      setDotTop(null);
      return;
    }
    const container = containerRef.current;
    const update = () => {
      const item = itemRefs.current[active];
      if (!container || !item) return;
      const c = container.getBoundingClientRect();
      const i = item.getBoundingClientRect();
      setDotTop(i.top - c.top + i.height / 2 - 1.5);
    };
    update();
    const ro = container ? new ResizeObserver(update) : null;
    if (container && ro) ro.observe(container);
    window.addEventListener("resize", update);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [active, ticks]);

  return (
    <div className="hidden md:block" style={{ flex: `0 0 ${RAIL_W}px`, width: RAIL_W, alignSelf: "stretch" }}>
      <nav
        aria-label="Product navigation"
        style={{ position: "sticky", top: RAIL_TOP, height: `calc(100vh - ${RAIL_TOP}px)`, boxSizing: "border-box" }}
      >
        <div aria-hidden="true" style={{ position: "absolute", left: RAIL_GUTTER + 8, top: 90, bottom: 90, width: 1, background: DATUM }} />
        <div
          ref={containerRef}
          style={{
            position: "absolute", left: RAIL_GUTTER, top: 0, bottom: 0, right: RAIL_GUTTER,
            display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "84px 0",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: "absolute", left: 0, top: dotTop ?? 0, width: 3, height: 3, background: SIGNAL,
              opacity: dotTop === null ? 0 : 1,
              transition: `top ${DUR_2} ${EASE}, opacity ${DUR_1} linear`,
              pointerEvents: "none",
            }}
          />
          {ticks.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                type="button"
                ref={(el) => { itemRefs.current[t.id] = el; }}
                onClick={() => jumpTo(t.id)}
                aria-current={isActive ? "true" : undefined}
                style={{ display: "flex", alignItems: "center", gap: 14, height: 14, padding: 0, border: 0, background: "transparent", cursor: "pointer", textAlign: "left" }}
              >
                <span style={{ width: 3, height: 3, flex: "0 0 3px" }} />
                <span style={{ width: 6, height: 1, background: MARK }} />
                <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 500, letterSpacing: "0.06em", whiteSpace: "nowrap", color: isActive ? TXT_D1 : TXT_D2, transition: `color ${DUR_1} ${EASE}` }}>
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function ShowcaseRulerMobile({ ticks }) {
  const active = useActiveSection(ticks.map((t) => t.id));
  return (
    <div className="md:hidden" style={{ position: "sticky", top: RULER_TOP, zIndex: 29, height: RULER_H, background: CANVAS_DARK, borderBottom: `1px solid ${BORDER_D}` }}>
      <div aria-hidden="true" style={{ position: "absolute", left: 24, right: 24, top: 22, height: 1, background: DATUM }} />
      <div style={{ position: "absolute", left: 24, right: 24, top: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {ticks.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              aria-label={`${t.n} ${t.label}`}
              aria-current={isActive ? "true" : undefined}
              onClick={() => jumpTo(t.id)}
              style={{ fontFamily: mono, fontSize: 13, letterSpacing: "0.04em", color: isActive ? TXT_D1 : TXT_D2, background: CANVAS_DARK, border: 0, padding: "0 3px", cursor: "pointer" }}
            >
              {isActive ? `[${t.n}]` : t.n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* =========================================================================
   VIDEO GRID — four 16:9 tiles as a 2x2
   ========================================================================= */
function VideoTile({ src, poster }) {
  const yt = youTubeId(src);
  if (yt) {
    const params = new URLSearchParams({
      autoplay: "1", mute: "1", loop: "1", playlist: yt,
      controls: "0", playsinline: "1", modestbranding: "1", rel: "0",
    });
    return (
      <iframe
        src={`https://www.youtube.com/embed/${yt}?${params}`}
        title="Product demo video"
        allow="autoplay; encrypted-media"
        loading="lazy"
        aria-hidden="true"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", display: "block", border: 0 }}
      />
    );
  }
  return (
    <video src={src} poster={poster} autoPlay loop muted playsInline preload="metadata" aria-hidden="true" tabIndex={-1}
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
  );
}

function PlaceholderTile({ path }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: S.s8, boxSizing: "border-box" }}>
      <span style={{ fontFamily: mono, fontSize: 12, letterSpacing: "0.04em", color: TXT_D3, textAlign: "center", wordBreak: "break-all", lineHeight: 1.6 }}>
        {path}
      </span>
    </div>
  );
}

function VideoGrid({ product }) {
  const wrapRef = useRef(null);
  const [near, setNear] = useState(false);
  const motionOk = useMotionOk();
  const hasVideos = product.videos.length > 0;

  useEffect(() => {
    if (!hasVideos) return;
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setNear(e.isIntersecting), { rootMargin: "150px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasVideos]);

  const tiles = useMemo(
    () => (hasVideos ? product.videos.slice(0, VIDEO_TILES) : []),
    [hasVideos, product.videos]
  );
  const count = hasVideos ? tiles.length : VIDEO_TILES;

  // Staggers each tile's actual mount by 150ms per index, so the browser
  // isn't asked to initialize up to 4 heavy YouTube iframes (each its own
  // embed player + network negotiation) in the same instant. Perceived as
  // videos "coming to life" in quick succession rather than everything
  // stalling together — autoplay and the ambient-loop behaviour are
  // otherwise unchanged, this only delays *when* each tile starts.
  const STAGGER_MS = 100;
  const [readyCount, setReadyCount] = useState(0);

  useEffect(() => {
    if (!near || !motionOk) return;
    setReadyCount(0);
    const timers = tiles.map((_, i) =>
      window.setTimeout(() => setReadyCount((c) => Math.max(c, i + 1)), i * STAGGER_MS)
    );
    return () => timers.forEach(window.clearTimeout);
  }, [near, motionOk, tiles]);

  return (
    <div ref={wrapRef} className="grid grid-cols-1 sm:grid-cols-2 showcase-video-grid" style={{ gap: S.s6 }}>
      {Array.from({ length: count }, (_, i) => {
        const src = tiles[i];
        const path = src ?? expectedVideoPath(product.slug, i + 1);
        const tileReady = near && motionOk && i < readyCount;
        return (
          <div key={path} style={{ position: "relative", aspectRatio: "16 / 9", background: SURFACE_DARK, border: `1px solid ${BORDER_D}`, overflow: "hidden" }}>
            {src && tileReady ? (
              <VideoTile src={src} poster={posterFor(src)} />
            ) : src ? (
              <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: `url(${posterFor(src)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
            ) : (
              <PlaceholderTile path={path} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* =========================================================================
   IMAGE SLIDER — hand-rolled, no library, matching the source's own choice
   ========================================================================= */
function Slider({ product }) {
  const has = product.images.length > 0;
  const frames = has ? product.images : [];
  const count = has ? frames.length : SLIDER_FRAMES;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const motionOk = useMotionOk();

  const go = useCallback((delta) => setIndex((i) => (i + delta + count) % count), [count]);

  useEffect(() => {
    if (paused || !motionOk || count < 2) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % count), SLIDE_MS);
    return () => window.clearInterval(t);
  }, [paused, motionOk, count]);

  const arrowBtn = {
    width: 36, height: 36, display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: "transparent", border: `1px solid ${BORDER_D}`, color: TXT_D2, fontFamily: mono, fontSize: 13, cursor: "pointer",
  };

  return (
    <div
      className="showcase-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
        else if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      }}
    >
      <div role="group" aria-roledescription="carousel" aria-label={`${product.name} stills`} tabIndex={0} className="showcase-slider-frame"
        style={{ position: "relative", background: SURFACE_DARK, border: `1px solid ${BORDER_D}`, overflow: "hidden" }}>
        {Array.from({ length: count }, (_, i) => {
          const src = frames[i];
          const path = src ?? expectedImagePath(product.slug, i + 1);
          const on = i === index;
          return (
            <div key={path} aria-hidden={!on}
              style={{
                position: "absolute", inset: 0, opacity: on ? 1 : 0,
                transition: motionOk ? `opacity ${DUR_2} ${EASE}` : "none",
                pointerEvents: on ? "auto" : "none",
                ...(src ? { backgroundImage: `url(${src})`, backgroundSize: "cover", backgroundPosition: "center" } : {}),
              }}>
              {src ? null : <PlaceholderTile path={path} />}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: S.s4, display: "flex", alignItems: "center", gap: S.s4 }}>
        <button type="button" onClick={() => go(-1)} aria-label="Previous image" style={arrowBtn}>←</button>
        <button type="button" onClick={() => go(1)} aria-label="Next image" style={arrowBtn}>→</button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {Array.from({ length: count }, (_, i) => (
            <button key={i} type="button" onClick={() => setIndex(i)} aria-label={`Go to image ${i + 1} of ${count}`} aria-current={i === index ? "true" : undefined}
              style={{ width: 22, height: 22, padding: 0, border: 0, background: "transparent", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ display: "block", width: i === index ? 18 : 8, height: 2, background: i === index ? SIGNAL : MARK, transition: `width ${DUR_1} ${EASE}` }} />
            </button>
          ))}
        </div>
        <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 11, letterSpacing: "0.06em", color: TXT_D3 }}>
          {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

/* =========================================================================
   DOWNLOAD BUTTONS — every click opens the request-access dialog rather
   than linking straight to a file, gated the same way regardless of
   whether the asset exists yet (per Pramod's feedback, 2026-08-29:
   "on clicking deck/brochure — submit details then get details").
   ========================================================================= */
const btnBase = {
  height: 40, padding: `0 ${S.s6}`, display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontSize: 16, fontWeight: 500, textDecoration: "none", borderRadius: PILL, boxSizing: "border-box", whiteSpace: "nowrap",
  cursor: "pointer",
};

function DownloadButton({ product, assetType, label, variant, onRequest }) {
  return (
    <button type="button" onClick={() => onRequest(product, assetType)}
      style={{
        ...btnBase, border: 0,
        ...(variant === "primary"
          ? { background: PRIMARY_BG, color: PRIMARY_FG }
          : { background: "transparent", color: TXT_D1, border: `1px solid ${BORDER_D}` }),
      }}>
      {label}
    </button>
  );
}

/* =========================================================================
   PRODUCT SECTION
   ========================================================================= */
function ProductSection({ tabId, product, onRequestAsset }) {
  const id = sectionId(tabId, product.slug);
  return (
    <section id={id} className="showcase-anchor showcase-section" style={{ position: "relative" }}>
      <header className="showcase-head">
        <div className="showcase-head-copy">
          <h2 style={{ margin: 0, display: "flex", alignItems: "baseline", gap: S.s4, flexWrap: "wrap", fontFamily: sans, fontSize: 38, lineHeight: 1.1, fontWeight: 600, letterSpacing: "-0.02em", color: TXT_D1 }}>
            <span>{product.name}</span>
          </h2>
          <p style={{ margin: `${S.s6} 0 0`, maxWidth: 720, fontSize: 17, lineHeight: 1.55, color: TXT_D2, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" }}>
            {product.blurb}
          </p>
        </div>
        <div className="showcase-head-actions">
          <DownloadButton product={product} assetType="brochure" label="Download brochure" variant="primary" onRequest={onRequestAsset} />
          <DownloadButton product={product} assetType="deck" label="Download deck" variant="secondary" onRequest={onRequestAsset} />
        </div>
      </header>

      <div className="showcase-media">
        <div className="showcase-evidence">
          <VideoGrid product={product} />
        </div>
        <div className="showcase-stills">
          <Slider product={product} />
        </div>
      </div>
    </section>
  );
}

/* =========================================================================
   TAB BAR
   ========================================================================= */
function TabBar({ activeId, onSelect }) {
  return (
    <div className="showcase-tabbar" style={{ position: "sticky", zIndex: 30, height: TABBAR_H, background: SURFACE_DARK, borderTop: `1px solid ${BORDER_D}`, borderBottom: `1px solid ${BORDER_D}` }}>
      <div role="tablist" aria-label="Product families" className="no-scrollbar" style={{ height: "100%", display: "flex", alignItems: "stretch", gap: S.s8, padding: `0 ${S.s6}`, overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap" }}>
        {SHOWCASE_TABS.map((t) => {
          const on = t.id === activeId;
          return (
            <button key={t.id} type="button" role="tab" aria-selected={on} onClick={() => onSelect(t.id)}
              style={{ position: "relative", flex: "0 0 auto", padding: 0, background: "transparent", border: 0, cursor: "pointer", fontSize: 16, fontWeight: 500, color: on ? TXT_D1 : TXT_D2, transition: `color ${DUR_1} ${EASE}` }}
              onMouseEnter={(e) => { if (!on) e.currentTarget.style.color = TXT_D1; }}
              onMouseLeave={(e) => { if (!on) e.currentTarget.style.color = TXT_D2; }}>
              {t.label}
              {on ? <span aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, bottom: -1, height: 2, background: SIGNAL }} /> : null}
            </button>
          );
        })}
      </div>
      <div aria-hidden="true" className="md:hidden" style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: 48, pointerEvents: "none", background: `linear-gradient(to right, rgba(16,18,22,0) 0%, ${SURFACE_DARK} 85%)` }} />
    </div>
  );
}

/* =========================================================================
   REQUEST ACCESS DIALOG — gates brochure/deck downloads behind a short
   lead-capture form. Submitting emails the requester an auto-reply (with
   the download link once the asset exists — see showcaseMedia.js) and
   notifies the internal team, mirroring the partner-registration flow's
   dual-email pattern.
   ========================================================================= */
function RequestAccessDialog({ open, product, assetType, onClose }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", email: "", company: "" });
  }, [open]);

  if (!product) return null;
  const label = assetType === "deck" ? "deck" : "brochure";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Missing details", description: "Name and email are required." });
      return;
    }
    const assetPath = assetType === "deck" ? product.deck : product.brochure;
    setSubmitting(true);
    try {
      await client.post("/showcase/request", {
        name: form.name,
        company_email: form.email,
        company: form.company || undefined,
        product_name: product.name,
        product_slug: product.slug,
        asset_type: assetType,
        asset_path: assetPath,
      });
      if (assetPath) {
        // Trigger the browser download immediately — the email is a backup
        // copy of the same link, not the only way to get the file.
        const a = document.createElement("a");
        a.href = assetPath;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast({ title: "Download started", description: "We've also emailed you the link." });
      } else {
        toast({ title: "Request sent", description: "This asset isn't uploaded yet — we'll email it to you as soon as it is." });
      }
      onClose();
    } catch (err) {
      toast({ title: "Could not submit request", description: err?.response?.data?.detail || "" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#101216] border-white/10 text-[#F4F5F7]">
        <DialogHeader>
          <DialogTitle>Get the {product.name} {label}</DialogTitle>
          <DialogDescription className="text-[#A6ADB8]">
            Share your details — the download starts right after, and we'll email you the link too.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[#A6ADB8]">Name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-[#A6ADB8]">Work email</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-[#A6ADB8]">Company (optional)</Label>
            <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Download {label}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* =========================================================================
   PREVIOUS PRODUCT CARD — leaner than ProductSection by design (Pramod,
   2026-08-31): a single video slot instead of the 2x2 grid + slider, no
   brochure/deck, and three lines of info — the product's own website, its
   own contact, and a way to reach it through Visotonics.
   ========================================================================= */
function PreviousProductCard({ product }) {
  const videoPath = product.video ?? expectedVideoPath(`previous-products/${product.slug}`, 1);
  return (
    <section className="showcase-anchor showcase-section" style={{ position: "relative" }}>
      <header>
        <h2 style={{ margin: 0, fontFamily: sans, fontSize: 32, lineHeight: 1.1, fontWeight: 600, letterSpacing: "-0.02em", color: TXT_D1 }}>
          {product.name}
        </h2>
        <p style={{ margin: `${S.s4} 0 0`, maxWidth: 640, fontSize: 16, lineHeight: 1.55, color: TXT_D2 }}>
          {product.blurb}
        </p>
      </header>

      <div className="showcase-prev-body" style={{ marginTop: S.s6, display: "flex", gap: S.s6 }}>
        <div style={{ flex: "0 0 auto", width: "100%", maxWidth: 420, aspectRatio: "16 / 9", position: "relative", background: SURFACE_DARK, border: `1px solid ${BORDER_D}`, overflow: "hidden" }}>
          {product.video ? (
            <VideoTile src={product.video} poster={posterFor(product.video)} />
          ) : (
            <PlaceholderTile path={videoPath} />
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: S.s4, fontSize: 14 }}>
          <div>
            <span style={{ ...eyebrow(TXT_D3), display: "block", marginBottom: 4 }}>Website</span>
            <a href={product.website} target="_blank" rel="noopener noreferrer" style={{ color: TXT_D1, textDecoration: "underline" }}>
              {product.website.replace(/^https?:\/\//, "")}
            </a>
          </div>
          {product.contact ? (
            <div>
              <span style={{ ...eyebrow(TXT_D3), display: "block", marginBottom: 4 }}>Contact</span>
              <a href={`mailto:${product.contact}`} style={{ color: TXT_D1, textDecoration: "underline" }}>{product.contact}</a>
            </div>
          ) : null}
          <div>
            <span style={{ ...eyebrow(TXT_D3), display: "block", marginBottom: 4 }}>Connect through us</span>
            <a href={`mailto:contact@visotonics.com?subject=${encodeURIComponent(`Introduction — ${product.name}`)}`} style={{ color: TXT_D1, textDecoration: "underline" }}>
              contact@visotonics.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================================
   EMPTY STATE
   ========================================================================= */
function EmptyTab({ label }) {
  return (
    <div className="showcase-section" style={{ textAlign: "center", paddingLeft: S.s6, paddingRight: S.s6 }}>
      <span style={{ ...eyebrow(TXT_D2), fontSize: 12, display: "block" }}>{label}</span>
      <p style={{ margin: `${S.s6} 0 0`, fontSize: 18, lineHeight: 1.5, color: TXT_D3 }}>Nothing catalogued here yet.</p>
    </div>
  );
}

/* =========================================================================
   PAGE SHELL
   ========================================================================= */
export default function ShowcasePage() {
  const [tabId, setTabId] = useState(SHOWCASE_TABS[0].id);
  const tab = useMemo(() => SHOWCASE_TABS.find((t) => t.id === tabId) ?? SHOWCASE_TABS[0], [tabId]);
  const ticks = useMemo(() => ticksFor(tab), [tab]);
  const first = useRef(true);

  const [requestDialog, setRequestDialog] = useState(null); // { product, assetType } | null
  const onRequestAsset = useCallback((product, assetType) => setRequestDialog({ product, assetType }), []);
  const closeRequestDialog = useCallback(() => setRequestDialog(null), []);

  // noindex — this repo's SeoTags/seoForPath has no noindex mechanism to hook
  // into, and the page is unlinked/unfinished the same way the source's was,
  // so this sets and cleans up its own <meta name="robots"> rather than
  // adding noindex support for every page.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const target = ticks[0]?.id;
    if (!target) {
      window.scrollTo({ top: 0, behavior: reduced() ? "auto" : "smooth" });
      return;
    }
    const raf = requestAnimationFrame(() => jumpTo(target));
    return () => cancelAnimationFrame(raf);
  }, [ticks]);

  return (
    <div style={{ background: CANVAS_DARK, minHeight: "100vh" }}>
      <style>{`
        .showcase-tabbar { top: ${NAV_M}px; }
        .showcase-anchor { scroll-margin-top: ${SCROLL_MT_M}px; }
        .showcase-section { padding: ${S.s8} ${S.s6} ${S.s12}; border-top: 1px solid ${BORDER_D_STRONG}; }
        .showcase-head { display: flex; flex-direction: column; gap: ${S.s8}; }
        .showcase-head-copy { min-width: 0; }
        .showcase-head-actions { display: flex; flex-wrap: wrap; gap: ${S.s4}; }
        .showcase-media { margin-top: ${S.s6}; }
        .showcase-stills { margin-top: ${S.s6}; }
        .showcase-slider { display: flex; flex-direction: column; }
        .showcase-slider-frame { aspect-ratio: 16 / 9; }
        .showcase-prev-body { flex-direction: column; }

        @media (min-width: 768px) {
          .showcase-tabbar { top: ${NAV_D}px; }
          .showcase-anchor { scroll-margin-top: ${SCROLL_MT_D}px; }
          .showcase-section { padding: ${S.s8} ${S.s8} ${S.s12}; }
          .showcase-head { flex-direction: row; align-items: flex-end; justify-content: space-between; gap: ${S.s16}; }
          .showcase-head-actions { flex: 0 0 auto; }
          .showcase-media { margin-top: ${S.s8}; display: flex; align-items: stretch; justify-content: center; gap: ${S.s6}; flex-wrap: wrap; }
          .showcase-evidence { flex: 1 1 420px; min-width: 0; max-width: 864px; }
          .showcase-stills { margin-top: 0; flex: 1 1 300px; min-width: 0; max-width: 420px; display: flex; }
          .showcase-slider { flex: 1 1 auto; min-width: 0; }
          .showcase-video-grid { width: 100%; max-width: 864px; grid-template-columns: 1fr 1fr !important; }
          .showcase-video-grid > div { aspect-ratio: 16 / 9 !important; height: auto !important; }
          .showcase-slider-frame { aspect-ratio: 16 / 9; flex: 1 1 auto; min-height: 0; width: 100%; }
          .showcase-prev-body { flex-direction: row; align-items: flex-start; }
        }
        @media (min-width: 1500px) {
          .showcase-video-grid { grid-template-columns: 420px 420px !important; }
          .showcase-video-grid > div { aspect-ratio: unset !important; height: 236px !important; }
          .showcase-evidence { flex: 0 0 auto; }
          .showcase-stills { flex: 0 0 auto; }
          .showcase-slider { flex: 0 0 auto; width: 420px; }
          .showcase-slider-frame { aspect-ratio: unset; }
        }
        .no-scrollbar { scrollbar-width: none; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      <TabBar activeId={tabId} onSelect={setTabId} />

      {ticks.length > 0 ? <ShowcaseRulerMobile key={tab.id} ticks={ticks} /> : null}

      <div style={{ maxWidth: 1620, margin: "0 auto", display: "flex", alignItems: "flex-start" }}>
        {ticks.length > 0 ? <ShowcaseRailDesktop key={tab.id} ticks={ticks} /> : (
          <div aria-hidden="true" className="hidden md:block" style={{ flex: `0 0 ${RAIL_W}px` }} />
        )}

        <div style={{ position: "relative", flex: "1 1 auto", minWidth: 0, maxWidth: 1440 }}>
          {tab.id === "previous-products" ? (
            PREVIOUS_PRODUCTS.map((prod) => (
              <PreviousProductCard key={prod.slug} product={prod} />
            ))
          ) : tab.products.length === 0 ? (
            <EmptyTab label={`${tab.label} — nothing here yet`} />
          ) : (
            tab.products.map((prod) => (
              <ProductSection key={sectionId(tab.id, prod.slug)} tabId={tab.id} product={prod} onRequestAsset={onRequestAsset} />
            ))
          )}
        </div>
      </div>

      <RequestAccessDialog
        open={!!requestDialog}
        product={requestDialog?.product}
        assetType={requestDialog?.assetType}
        onClose={closeRequestDialog}
      />
    </div>
  );
}
